/**
 * EventDialog - Create/edit life events
 */

import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { LifeEvent, LifeEventType, LifeEventCategory, FinancialImpact } from '@/types'
import { EVENT_CATEGORY_MAP, EVENT_ICONS } from '@/types/timeline.types'
import * as LucideIcons from 'lucide-react'
import { DollarSign, Calendar, Trash2 } from 'lucide-react'

// Event type options grouped by category
const EVENT_TYPE_OPTIONS: { category: LifeEventCategory; types: { value: LifeEventType; label: string }[] }[] = [
  {
    category: 'career',
    types: [
      { value: 'career_start', label: 'Start Career' },
      { value: 'promotion', label: 'Promotion' },
      { value: 'job_change', label: 'Job Change' },
      { value: 'salary_increase', label: 'Salary Increase' },
      { value: 'sabbatical', label: 'Sabbatical' },
      { value: 'career_end', label: 'Leave Job' },
    ],
  },
  {
    category: 'family',
    types: [
      { value: 'marriage', label: 'Marriage' },
      { value: 'divorce', label: 'Divorce' },
      { value: 'child_born', label: 'Child Born' },
      { value: 'child_college_start', label: 'Child Starts College' },
      { value: 'child_college_end', label: 'Child Finishes College' },
      { value: 'child_independent', label: 'Child Becomes Independent' },
    ],
  },
  {
    category: 'housing',
    types: [
      { value: 'home_purchase', label: 'Buy Home' },
      { value: 'home_sale', label: 'Sell Home' },
      { value: 'renovation', label: 'Home Renovation' },
      { value: 'relocation', label: 'Relocate' },
    ],
  },
  {
    category: 'education',
    types: [
      { value: 'education_start', label: 'Start Education' },
      { value: 'education_end', label: 'Graduate' },
    ],
  },
  {
    category: 'retirement',
    types: [
      { value: 'retirement_start', label: 'Retire' },
      { value: 'social_security_start', label: 'Start Social Security' },
      { value: 'pension_start', label: 'Start Pension' },
      { value: 'rmd_start', label: 'Start RMDs' },
    ],
  },
  {
    category: 'financial',
    types: [
      { value: 'inheritance', label: 'Receive Inheritance' },
      { value: 'windfall', label: 'Windfall' },
      { value: 'major_purchase', label: 'Major Purchase' },
      { value: 'debt_payoff', label: 'Debt Payoff' },
      { value: 'goal_achieved', label: 'Goal Achieved' },
    ],
  },
  {
    category: 'health',
    types: [
      { value: 'disability', label: 'Disability Event' },
      { value: 'long_term_care', label: 'Long-term Care' },
    ],
  },
  {
    category: 'custom',
    types: [
      { value: 'custom', label: 'Custom Event' },
    ],
  },
]

interface EventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  event?: Partial<LifeEvent>
  onSave: (event: Omit<LifeEvent, 'id' | 'isPast' | 'isAutoGenerated'>) => void
  onDelete?: () => void
}

export function EventDialog({ open, onOpenChange, event, onSave, onDelete }: EventDialogProps) {
  const isEditing = !!event?.id
  
  // Form state
  const [type, setType] = React.useState<LifeEventType>(event?.type || 'custom')
  const [name, setName] = React.useState(event?.name || '')
  const [description, setDescription] = React.useState(event?.description || '')
  const [date, setDate] = React.useState(event?.date || '')
  const [confidence, setConfidence] = React.useState<'certain' | 'likely' | 'possible'>(event?.confidence || 'likely')
  
  // Financial impact
  const [oneTimeIncome, setOneTimeIncome] = React.useState(event?.financialImpact?.oneTimeIncome || 0)
  const [oneTimeExpense, setOneTimeExpense] = React.useState(event?.financialImpact?.oneTimeExpense || 0)
  const [incomeChange, setIncomeChange] = React.useState(event?.financialImpact?.incomeChange || 0)
  const [expenseChange, setExpenseChange] = React.useState(event?.financialImpact?.expenseChange || 0)
  
  // Reset form when event changes
  React.useEffect(() => {
    if (open) {
      setType(event?.type || 'custom')
      setName(event?.name || '')
      setDescription(event?.description || '')
      setDate(event?.date || '')
      setConfidence(event?.confidence || 'likely')
      setOneTimeIncome(event?.financialImpact?.oneTimeIncome || 0)
      setOneTimeExpense(event?.financialImpact?.oneTimeExpense || 0)
      setIncomeChange(event?.financialImpact?.incomeChange || 0)
      setExpenseChange(event?.financialImpact?.expenseChange || 0)
    }
  }, [open, event])
  
  // Get icon for current type
  const IconComponent = EVENT_ICONS[type] 
    ? (LucideIcons[EVENT_ICONS[type] as keyof typeof LucideIcons] as React.ComponentType<{ className?: string }>)
    : null
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const financialImpact: FinancialImpact = {}
    if (oneTimeIncome > 0) financialImpact.oneTimeIncome = oneTimeIncome
    if (oneTimeExpense > 0) financialImpact.oneTimeExpense = oneTimeExpense
    if (incomeChange !== 0) financialImpact.incomeChange = incomeChange
    if (expenseChange !== 0) financialImpact.expenseChange = expenseChange
    
    onSave({
      type,
      category: EVENT_CATEGORY_MAP[type],
      name: name || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      description,
      date,
      confidence,
      financialImpact,
      isRecurring: false,
    })
    
    onOpenChange(false)
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {IconComponent && <IconComponent className="h-5 w-5" />}
              {isEditing ? 'Edit Event' : 'Add Life Event'}
            </DialogTitle>
            <DialogDescription>
              Add an event to your life timeline. This will affect your wealth projections.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Event Type */}
            <div className="space-y-2">
              <Label htmlFor="type">Event Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as LifeEventType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPE_OPTIONS.map((group) => (
                    <React.Fragment key={group.category}>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground capitalize">
                        {group.category}
                      </div>
                      {group.types.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Event Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Buy first home"
              />
            </div>
            
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            {/* Confidence */}
            <div className="space-y-2">
              <Label htmlFor="confidence">How certain is this event?</Label>
              <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="certain">Certain (already planned)</SelectItem>
                  <SelectItem value="likely">Likely (planning on it)</SelectItem>
                  <SelectItem value="possible">Possible (might happen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Notes (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add any additional details..."
                rows={2}
              />
            </div>
            
            {/* Financial Impact */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-base font-semibold">Financial Impact</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="oneTimeIncome" className="text-xs">One-time Income</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="oneTimeIncome"
                      type="number"
                      value={oneTimeIncome || ''}
                      onChange={(e) => setOneTimeIncome(Number(e.target.value) || 0)}
                      className="pl-9"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="oneTimeExpense" className="text-xs">One-time Expense</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="oneTimeExpense"
                      type="number"
                      value={oneTimeExpense || ''}
                      onChange={(e) => setOneTimeExpense(Number(e.target.value) || 0)}
                      className="pl-9"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="incomeChange" className="text-xs">Annual Income Change</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="incomeChange"
                      type="number"
                      value={incomeChange || ''}
                      onChange={(e) => setIncomeChange(Number(e.target.value) || 0)}
                      className="pl-9"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="expenseChange" className="text-xs">Annual Expense Change</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="expenseChange"
                      type="number"
                      value={expenseChange || ''}
                      onChange={(e) => setExpenseChange(Number(e.target.value) || 0)}
                      className="pl-9"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Use negative numbers for decreases (e.g., -20000 for income reduction)
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            {isEditing && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  onDelete()
                  onOpenChange(false)
                }}
                className="mr-auto"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Save Changes' : 'Add Event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
